/**
 * @fileoverview Utilities for mapping VAT selections to override payloads.
 * @module features/payments/utils/vatOverride
 */

/**
 * VAT selection state captured from the UI layer.
 */
export interface VatSelectionState {
  vatApplicable: boolean
  reverseCharge: boolean
  vatRate: number | null
}

export interface VatMutationPayload {
  apiPayload: {
    vatApplicable?: boolean
    reverseCharge?: boolean
    vatRate?: number
  }
  nextState: VatSelectionState
}

/**
 * Convert a VAT selection into API payload + next state.
 *
 * @param selection Normalized VAT selection from the UI
 * @returns Payload and next state used by the table to update cached data
 * @example
 * ```ts
 * buildVatOverridePayload({ vatApplicable: true, reverseCharge: false, vatRate: 0.23 })
 * ```
 */
export function buildVatOverridePayload(selection: VatSelectionState): VatMutationPayload {
  if (!selection.vatApplicable) {
    return {
      apiPayload: {
        vatApplicable: false,
        reverseCharge: false,
      },
      nextState: { ...selection, reverseCharge: false, vatRate: null },
    }
  }

  return {
    apiPayload: {
      vatApplicable: true,
      reverseCharge: selection.reverseCharge,
      ...(typeof selection.vatRate === 'number' ? { vatRate: selection.vatRate } : {}),
    },
    nextState: {
      vatApplicable: true,
      reverseCharge: selection.reverseCharge,
      vatRate: selection.vatRate ?? 0,
    },
  }
}
